package br.com.htms.inspeccaocampo.export

import br.com.htms.inspeccaocampo.data.apr.AprAssinatura
import br.com.htms.inspeccaocampo.data.apr.AprCatalog
import br.com.htms.inspeccaocampo.data.apr.AprDados
import br.com.htms.inspeccaocampo.data.apr.AprEtapa
import br.com.htms.inspeccaocampo.data.apr.AprSecao
import br.com.htms.inspeccaocampo.platform.PlatformServices
import kotlin.math.max
import kotlin.math.min

/** APR em PDF A4 paisagem, com as colunas, cores e blocos da planilha modelo. */
object AprPdfBuilder {
    private const val W = 842.0
    private const val H = 595.0
    private const val X0 = 18.0
    private const val X1 = W - 18.0
    private const val TOPO = H - 26.0
    private const val BASE = 26.0
    private const val PAD = 2.5
    private const val FS = 6.8
    private const val LOGO_ASSET = "os_logo_htms.jpg"

    private const val CINZA = 0xD9D9D9
    private const val CINZA_CLARO = 0xF2F2F2
    private const val AZUL_CLARO = 0xDAE3F3

    private enum class Alinh { ESQ, CENTRO }

    private class Cel(
        val de: String,
        val ate: String,
        val texto: String,
        val bold: Boolean = false,
        val fundo: Int? = null,
        val alinh: Alinh = Alinh.CENTRO,
        val size: Double = FS,
        val topo: Boolean = false
    )

    private val colunas = AprXlsxBuilder.LARGURAS.keys.toList()
    private val escala = (X1 - X0) / AprXlsxBuilder.LARGURAS.values.sum()
    private fun xDe(col: String): Double =
        X0 + colunas.take(colunas.indexOf(col)).sumOf { AprXlsxBuilder.LARGURAS.getValue(it) } * escala
    private fun xAte(col: String): Double = xDe(col) + AprXlsxBuilder.LARGURAS.getValue(col) * escala

    suspend fun build(d: AprDados): ByteArray {
        val logo = PlatformServices.loadBundledAsset(LOGO_ASSET)
            ?.takeIf { it.size > 2 && it[0] == 0xFF.toByte() && it[1] == 0xD8.toByte() }
        return build(d, logo)
    }

    fun build(d: AprDados, logo: ByteArray?): ByteArray = Desenho(d, PdfPagina(W, H, logo)).gerar()

    private class Desenho(val d: AprDados, val pg: PdfPagina) {
        var y = TOPO

        fun gerar(): ByteArray {
            cabecalhoPagina()
            identificacao()
            tecnica()
            assinaturas()
            executantes()
            return pg.finish("APR ${d.numeroApr}")
        }

        private fun leading(size: Double) = size * 1.22

        private fun linhasDe(c: Cel): List<String> {
            val w = max(4.0, xAteC(c) - xDeC(c) - 2 * PAD)
            return if (c.texto.isBlank()) emptyList() else PdfTexto.wrap(c.texto, w, c.size, c.bold)
        }

        private fun xDeC(c: Cel) = AprPdfBuilder.xDe(c.de)
        private fun xAteC(c: Cel) = AprPdfBuilder.xAte(c.ate)

        private fun alturaLinha(cels: List<Cel>, minimo: Double): Double =
            max(minimo, cels.maxOf { c -> linhasDe(c).size * leading(c.size) + 2 * PAD + 1 })

        private fun novaPagina() {
            pg.newPage()
            y = TOPO
            cabecalhoPagina()
        }

        /** Garante [h] pontos livres na página atual (quebra se precisar). */
        private fun reservar(h: Double): Boolean {
            if (y - h >= BASE) return false
            novaPagina()
            return true
        }

        private fun linha(cels: List<Cel>, minimo: Double = 12.0, aposQuebra: (() -> Unit)? = null) {
            val h = alturaLinha(cels, minimo)
            if (reservar(h)) aposQuebra?.invoke()
            desenhar(cels, h)
        }

        private fun desenhar(cels: List<Cel>, h: Double) {
            val bottom = y - h
            cels.forEach { c ->
                val xa = xDeC(c)
                val xb = xAteC(c)
                c.fundo?.let { pg.fill(xa, bottom, xb - xa, h, it) }
                pg.rect(xa, bottom, xb - xa, h, 0.5)
                val ls = linhasDe(c)
                val lead = leading(c.size)
                val bloco = ls.size * lead
                var base = if (c.topo) y - PAD - c.size else bottom + (h + bloco) / 2 - c.size
                ls.forEach { t ->
                    if (c.alinh == Alinh.CENTRO) pg.textCentered(t, xa + PAD, xb - PAD, base, c.size, c.bold)
                    else pg.text(t, xa + PAD, base, c.size, c.bold)
                    base -= lead
                }
            }
            y = bottom
        }

        private fun espaco(h: Double) { y -= h }

        private fun cabecalhoPagina() {
            pg.text("PEX PADRÃO DOSSA - VERSÃO 01", X1 - PdfTexto.largura("PEX PADRÃO DOSSA - VERSÃO 01", 6.0, true), H - 14.0, 6.0, true, 0x0070C0)
            pg.textCentered("Classificação: Pública", X0, X1, 12.0, 6.5, false, 0x008000)
            val h1 = 14.0
            val h2 = 16.0
            val topo = y
            // Logo (B:G) e título (H:Y) ocupam as duas linhas.
            val xa = xDe("B")
            val xb = xAte("G")
            pg.rect(xa, topo - h1 - h2, xb - xa, h1 + h2, 0.5)
            pg.logoSize?.let { s ->
                val lh = h1 + h2 - 6
                val lw = min(lh * s.width / s.height.toDouble(), xb - xa - 8)
                val lhA = lw * s.height / s.width.toDouble()
                pg.logo(xa + (xb - xa - lw) / 2, topo - h1 - h2 + (h1 + h2 - lhA) / 2, lw, lhA)
            }
            val ta = xDe("H")
            val tb = xAte("Y")
            pg.rect(ta, topo - h1 - h2, tb - ta, h1 + h2, 0.5)
            pg.textCentered("APR - ANÁLISE PRELIMINAR DE RISCOS", ta, tb, topo - (h1 + h2) / 2 - 4, 11.0, true)
            y = topo
            desenharParcial(
                listOf(
                    Cel("Z", "AB", "Nº APR:", bold = true, fundo = CINZA, alinh = Alinh.ESQ),
                    Cel("AC", "AF", d.numeroApr, bold = true, fundo = CINZA),
                    Cel("AG", "AG", listOf("HTMS ENERGIA", d.ano).filter { it.isNotBlank() }.joinToString(" "), bold = true, fundo = CINZA, size = 6.5)
                ),
                h1
            )
            desenharParcial(
                listOf(
                    Cel("Z", "AB", "Nº SI/OS:", bold = true, fundo = CINZA, alinh = Alinh.ESQ),
                    Cel("AC", "AG", d.numeroSiOs, bold = true)
                ),
                h2
            )
            y = topo - h1 - h2 - 4
        }

        private fun desenharParcial(cels: List<Cel>, h: Double) = desenhar(cels, h)

        private fun titulo(texto: String) = linha(listOf(Cel("B", "AG", texto, bold = true, fundo = CINZA, size = 8.0)), 13.0)

        private fun rotuloValor(rotulo: String, valor: String) = linha(
            listOf(
                Cel("B", "F", rotulo, bold = true, fundo = CINZA_CLARO, alinh = Alinh.ESQ),
                Cel("G", "AG", valor, alinh = Alinh.ESQ)
            )
        )

        private fun identificacao() {
            titulo("IDENTIFICAÇÃO DA ATIVIDADE")
            espaco(3.0)
            rotuloValor("ATIVIDADE A SER REALIZADA CONFORME PLANEJAMENTO:", d.atividade)
            linha(
                listOf(
                    Cel("B", "F", "INSTALAÇÃO:", bold = true, fundo = CINZA_CLARO, alinh = Alinh.ESQ),
                    Cel("G", "T", d.instalacao, alinh = Alinh.ESQ),
                    Cel("U", "W", "LOCAL/ EQUIPAMENTO:", bold = true, fundo = CINZA_CLARO, alinh = Alinh.ESQ),
                    Cel("X", "AG", d.local, alinh = Alinh.ESQ)
                )
            )
            linha(
                listOf(
                    Cel("B", "F", "EMISSÃO:", bold = true, fundo = CINZA_CLARO, alinh = Alinh.ESQ),
                    Cel("G", "N", d.emissao),
                    Cel("O", "P", "Hora:", bold = true, fundo = CINZA_CLARO),
                    Cel("Q", "T", d.horaFormatada, alinh = Alinh.ESQ),
                    Cel("U", "W", "VIGÊNCIA:", bold = true, fundo = CINZA_CLARO, alinh = Alinh.ESQ),
                    Cel("X", "AG", d.vigencia, alinh = Alinh.ESQ)
                )
            )
            rotuloValor("RESPONSÁVEL:", d.responsavel)
            rotuloValor("RESPONSÁVEIS SUBSTITUTOS:", d.substitutos)
            rotuloValor("MÁQUINAS E EQUIPAMENTOS:", d.maquinas)
            rotuloValor("EPI's OBRIGATÓRIOS:", d.episObrigatorios)
            rotuloValor("EPI's EVENTUAIS:", d.episEventuais)
            rotuloValor("EPC's OBRIGATÓRIOS:", d.epcsObrigatorios)
            rotuloValor("EPC's EVENTUAIS:", d.epcsEventuais)
            espaco(4.0)
        }

        private val cabecalhoTecnica = listOf(
            Cel("B", "B", "ITEM", bold = true, size = 7.2),
            Cel("C", "D", "ITEM DO PEX", bold = true, size = 7.2),
            Cel("E", "J", "ETAPAS DA TAREFA\n(PASSO A PASSO DA TAREFA)", bold = true, size = 7.2),
            Cel("K", "M", "PERIGOS E RISCOS", bold = true, size = 7.2),
            Cel("N", "N", "PAC", bold = true, size = 7.2),
            Cel("O", "V", "MEDIDAS DE CONTROLE", bold = true, size = 7.2),
            Cel("W", "AA", "O QUE PODE DAR ERRADO\n(o que pode comprometer a efetividade das medidas de controle)", bold = true, size = 6.5),
            Cel("AB", "AG", "MEDIDAS DE PROTEÇÃO ADICIONAL\n(o que deve ser feito para manter a efetividade das medidas de controle)", bold = true, size = 6.5)
        )

        private fun tecnica() {
            titulo("DESCRIÇÃO DA TÉCNICA / DEFINIÇÃO DE RESPONSABILIDADES")
            val hCab = alturaLinha(cabecalhoTecnica, 22.0)
            reservar(hCab + 30.0)
            desenhar(cabecalhoTecnica, hCab)
            d.etapas.ifEmpty { listOf(AprEtapa()) }.forEachIndexed { i, e ->
                linha(
                    listOf(
                        Cel("B", "B", (i + 1).toString()),
                        Cel("C", "D", e.itemPex),
                        Cel("E", "J", e.etapas),
                        Cel("K", "M", e.perigos),
                        Cel("N", "N", e.pac),
                        Cel("O", "V", e.medidas),
                        Cel("W", "AA", e.oQuePodeDarErrado),
                        Cel("AB", "AG", e.medidasAdicionais, bold = true)
                    ),
                    minimo = 24.0
                ) { desenhar(cabecalhoTecnica, hCab) }
            }
            val obs = "Observações complementares:" + if (d.observacoes.isBlank()) "" else " ${d.observacoes.trim()}"
            linha(listOf(Cel("B", "AG", obs, bold = false, alinh = Alinh.ESQ, size = 7.2, topo = true)), 34.0)
            espaco(4.0)
        }

        private fun blocoAssinatura(a: AprAssinatura) {
            val p = d.pessoa(a)
            val papel = listOf(Cel("B", "AG", a.rotulo, fundo = CINZA_CLARO, size = 7.2))
            val cab = listOf(
                Cel("B", "J", "Nome"), Cel("K", "S", "Matrícula"), Cel("T", "AA", "Assinatura"), Cel("AB", "AG", a.rotuloData)
            )
            val valores = listOf(
                Cel("B", "J", p.nome, fundo = AZUL_CLARO), Cel("K", "S", p.matricula, fundo = AZUL_CLARO),
                Cel("T", "AA", "", fundo = AZUL_CLARO), Cel("AB", "AG", "", fundo = AZUL_CLARO)
            )
            val h1 = alturaLinha(papel, 12.0)
            val h3 = alturaLinha(valores, 16.0)
            reservar(h1 + 12.0 + h3)
            desenhar(papel, h1)
            desenhar(cab, 12.0)
            desenhar(valores, h3)
        }

        private fun assinaturas() {
            AprSecao.entries.forEach { secao ->
                val cab = listOf(Cel("B", "AG", secao.titulo, bold = true, fundo = CINZA, size = 8.0))
                val decl = listOf(Cel("B", "AG", secao.declaracao, fundo = AZUL_CLARO, alinh = Alinh.ESQ))
                reservar(alturaLinha(cab, 13.0) + alturaLinha(decl, 12.0) + 40.0)
                linha(cab, 13.0)
                linha(decl)
                AprAssinatura.entries.filter { it.secao == secao }.forEach { blocoAssinatura(it) }
                espaco(4.0)
            }
        }

        private fun executantes() {
            val cab = listOf(
                Cel("B", "C", "ITEM", bold = true, fundo = CINZA), Cel("D", "O", "NOME", bold = true, fundo = CINZA),
                Cel("P", "AA", "EMPRESA", bold = true, fundo = CINZA), Cel("AB", "AG", "ASSINATURA", bold = true, fundo = CINZA)
            )
            val texto = listOf(Cel("B", "AG", AprCatalog.TEXTO_EXECUTANTES, size = 7.2))
            reservar(13.0 + alturaLinha(texto, 12.0) + 12.0 + 16.0 * 3)
            titulo("RELAÇÃO DOS EXECUTANTES DA TAREFA")
            linha(texto)
            desenhar(cab, 12.0)
            val n = max(17, d.executantes.size)
            for (i in 0 until n) {
                val ex = d.executantes.getOrNull(i)
                linha(
                    listOf(
                        Cel("B", "C", (i + 1).toString(), bold = true),
                        Cel("D", "O", ex?.nome.orEmpty(), bold = true),
                        Cel("P", "AA", ex?.empresa.orEmpty(), bold = true),
                        Cel("AB", "AG", "")
                    ),
                    minimo = 16.0
                ) { desenhar(cab, 12.0) }
            }
        }
    }
}
